import { createFileRoute, redirect } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { AnimatePresence } from 'motion/react'

import { TaxFactoryInputPanel } from './-components/TaxFactoryInputPanel'
import { TaxFactorySimulator } from './-components/TaxFactorySimulator'
import { TaxFactoryHero } from './-components/TaxFactoryHero'
import type {
  TaxFactoryInputs,
  TaxFactoryResult,
} from '@/types/tax-factory.types'
import { PageTitle } from '@/components/PageTitle'
import {
  DEFAULT_INPUTS,
  TaxFactoryStep,
  isInputsValid,
} from '@/types/tax-factory.types'
import { runSimulation } from '@/lib/tax-factory-engine'

export const Route = createFileRoute('/_app/tools/tax-factory/')({
  beforeLoad: () => {
    if (import.meta.env.VITE_ENABLE_TAX_FACTORY !== 'true') {
      throw redirect({ to: '/tools' })
    }
  },
  component: TaxFactoryPage,
})

function TaxFactoryPage() {
  // ─── Form State ──────────────────────────────────────────────────────────
  const [inputs, setInputs] = useState<TaxFactoryInputs>(DEFAULT_INPUTS)

  // ─── UI State ────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<TaxFactoryStep>(
    TaxFactoryStep.PEOPLE_TIMELINE,
  )
  const [result, setResult] = useState<TaxFactoryResult | null>(null)
  const [showResults, setShowResults] = useState(false)

  // ─── Computed ────────────────────────────────────────────────────────────
  const isValid = useMemo(() => isInputsValid(inputs), [inputs])

  // ─── Handlers ────────────────────────────────────────────────────────────
  const updateInput = useCallback(
    <TKey extends keyof TaxFactoryInputs>(
      field: TKey,
      value: TaxFactoryInputs[TKey],
    ) => {
      setInputs((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const nextStep = () =>
    setCurrentStep(
      (prev) =>
        Math.min(prev + 1, TaxFactoryStep.CONVERSION_PLAN) as TaxFactoryStep,
    )
  const prevStep = () =>
    setCurrentStep(
      (prev) =>
        Math.max(prev - 1, TaxFactoryStep.PEOPLE_TIMELINE) as TaxFactoryStep,
    )

  const handleSimulate = useCallback(() => {
    if (!isInputsValid(inputs)) return
    const simulation = runSimulation(inputs)
    setResult(simulation)
    setShowResults(true)
  }, [inputs])

  const handleReset = useCallback(() => {
    setResult(null)
    setShowResults(false)
    setCurrentStep(TaxFactoryStep.PEOPLE_TIMELINE)
  }, [])

  return (
    <div className="flex flex-col min-h-0">
      <PageTitle title="Tax Factory" />

      <div className="flex gap-4 p-4 flex-col lg:flex-row">
        {/* Wizard Panel */}
        <div className="w-full lg:w-96 flex-shrink-0">
          <TaxFactoryInputPanel
            inputs={inputs}
            updateInput={updateInput}
            currentStep={currentStep}
            onNext={nextStep}
            onPrevious={prevStep}
            onSimulate={handleSimulate}
            isValid={isValid}
            showResults={showResults}
            onReset={handleReset}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {!showResults && (
              <TaxFactoryHero key="hero" currentStep={currentStep} />
            )}

            {showResults && result && (
              <TaxFactorySimulator
                key="simulator"
                result={result}
                inputs={inputs}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
